import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
  selectGameObject,
  selectGameStatusObject,
} from "../../gamestate/gameStateSlice";
import { GAMESTATE_ROUND_FINAL } from "../../gamestate/constants";
import { selectAllPlayers, makeSelectPlayersReadinessForInterRound } from "../../player/playerSlice";
import { advanceState } from "../../gamestate/GameState";
import { determineNextRound, getRoundDisplayName } from "../../gamestate/roundCompletionUtils";
import BackgroundEffect from "features/components/BackgroundEffect";

const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';
const SIGNATURE_BG = '#1a7a72';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Root = styled('div')({
  width: "100%",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "24px 40px",
  boxSizing: "border-box",
});

const Title = styled(Typography)({
  fontFamily: "'Gyparody', 'Bangers', 'Impact', sans-serif",
  fontWeight: 800,
  fontSize: '3rem',
  color: TEXT_COLOR,
  textAlign: "center",
  textShadow: '1px 1px 0px rgba(46, 196, 182, 0.3)',
  animation: `${fadeInUp} 0.5s ease-out`,
  flexShrink: 0,
});

const Subtitle = styled(Typography)({
  fontSize: '1.1rem',
  color: TEXT_COLOR,
  textAlign: "center",
  marginBottom: "20px",
  opacity: 0.6,
  animation: `${fadeInUp} 0.5s ease-out 0.1s both`,
  flexShrink: 0,
});

const PlayerGrid = styled(Box)({
  display: "grid",
  gap: "12px",
  width: "100%",
  flex: "1 1 auto",
  minHeight: 0,
  overflow: "auto",
  alignContent: "center",
  justifyContent: "center",
  animation: `${fadeInUp} 0.5s ease-out 0.2s both`,
  padding: "0 20px",
});

const PlayerCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'ready',
})(({ ready }) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: "12px",
  backgroundColor: ready ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
  border: ready ? `2px solid ${ACCENT_TEAL}` : '2px solid transparent',
  boxShadow: ready ? '0 2px 12px rgba(46, 196, 182, 0.25)' : '0 1px 4px rgba(0,0,0,0.04)',
  opacity: ready ? 1 : 0.5,
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  minWidth: 0,
}));

const SignatureThumb = styled('img')({
  width: '100%',
  height: 80,
  objectFit: 'contain',
  backgroundColor: SIGNATURE_BG,
  display: 'block',
});

const SignaturePlaceholder = styled(Box)({
  width: '100%',
  height: 80,
  backgroundColor: SIGNATURE_BG,
});

const PlayerInfo = styled(Box)({
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  minWidth: 0,
});

const PlayerName = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.15rem',
  color: TEXT_COLOR,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0,
});

const PlayerScore = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.15rem',
  color: ACCENT_TEAL,
  flexShrink: 0,
});

const ReadyIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'ready',
})(({ ready }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  color: ready ? ACCENT_TEAL : 'rgba(0,0,0,0.15)',
  transition: 'color 0.3s ease',
}));

const BottomBar = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "10px",
  paddingTop: "16px",
  flexShrink: 0,
  animation: `${fadeInUp} 0.5s ease-out 0.3s both`,
});

const StartButton = styled(Button)({
  background: `linear-gradient(120deg, ${ACCENT_YELLOW} 0%, ${ACCENT_TEAL} 50%, ${ACCENT_YELLOW} 100%)`,
  backgroundSize: '200% 200%',
  backgroundPosition: '0% 40%',
  color: TEXT_COLOR,
  fontSize: '1.3rem',
  padding: "14px 56px",
  fontWeight: 700,
  textTransform: 'none',
  borderRadius: '12px',
  boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  transition: 'background-position 0.5s cubic-bezier(0.4,0,0.2,1), box-shadow 0s',
  '&:hover': {
    backgroundPosition: '100% 60%',
    boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  },
});

const ReadyCount = styled(Typography)({
  fontSize: '1rem',
  color: TEXT_COLOR,
  opacity: 0.6,
  fontWeight: 500,
});

// Determine grid columns based on player count
const getGridColumns = (count) => {
  if (count <= 4) return 2;
  if (count <= 6) return 3;
  if (count <= 12) return 4;
  if (count <= 18) return 5;
  if (count <= 24) return 6;
  return 7;
};

const InterRoundLobby = () => {
  const dispatch = useDispatch();
  const game = useSelector(selectGameObject);
  const gameStatus = useSelector(selectGameStatusObject);
  const players = useSelector(selectAllPlayers);

  const round = gameStatus.round;
  const selectReadiness = useMemo(() => makeSelectPlayersReadinessForInterRound(round), [round]);
  const readiness = useSelector(selectReadiness);

  const readyCount = Object.values(readiness).filter(p => p.isReady).length;
  const totalCount = players.length;
  const allReady = readyCount === totalCount && totalCount > 0;

  const nextRound = determineNextRound(round, game?.categories);
  // Sort players by score descending (when scores exist), otherwise alphabetical
  const hasScores = useMemo(() => players.some(p => p.score !== 0), [players]);
  const sortedPlayers = useMemo(() => {
    return [...players].sort((a, b) => {
      if (hasScores) return (b.score || 0) - (a.score || 0);
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [players, hasScores]);

  // Auto-advance when all players are ready
  // Skip auto-advance when next round is final — FinalIntro moment handles the transition
  useEffect(() => {
    if (allReady && nextRound !== GAMESTATE_ROUND_FINAL) {
      const timer = setTimeout(async () => {
        await advanceState(game, players, dispatch);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [allReady, nextRound, game, players, dispatch]);

  const handleStartNextRound = async () => {
    await advanceState(game, players, dispatch);
  };

  const completedRound = getRoundDisplayName(round);
  const nextRoundLabel = nextRound
    ? `Start ${getRoundDisplayName(nextRound)}`
    : 'Continue';

  const columns = getGridColumns(totalCount);

  return (
    <Root>
      <BackgroundEffect />

      <Title>Round Complete!</Title>
      <Subtitle>{completedRound} has ended</Subtitle>

      <PlayerGrid sx={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 280px))` }}>
        {sortedPlayers.map(player => {
          const playerReady = readiness[player.id]?.isReady;
          return (
            <PlayerCard key={player.id} ready={playerReady}>
              {player.signatureURL ? (
                <SignatureThumb
                  src={player.signatureURL}
                  alt={player.name}
                />
              ) : (
                <SignaturePlaceholder />
              )}
              <PlayerInfo>
                <PlayerName>{player.name}</PlayerName>
                {hasScores && (
                  <PlayerScore>
                    {player.score >= 0
                      ? "$" + player.score.toLocaleString()
                      : "-$" + Math.abs(player.score).toLocaleString()}
                  </PlayerScore>
                )}
                <ReadyIcon ready={playerReady}>
                  {playerReady
                    ? <CheckCircleIcon />
                    : <HourglassEmptyIcon />
                  }
                </ReadyIcon>
              </PlayerInfo>
            </PlayerCard>
          );
        })}
      </PlayerGrid>

      <BottomBar>
        {allReady ? (
          <ReadyCount sx={{ color: ACCENT_TEAL, fontWeight: 600, opacity: 1 }}>
            All players ready! Starting next round...
          </ReadyCount>
        ) : totalCount > 0 ? (
          <ReadyCount>
            {readyCount} of {totalCount} players ready
          </ReadyCount>
        ) : null}

        <StartButton onClick={handleStartNextRound}>
          {nextRoundLabel}
        </StartButton>
      </BottomBar>
    </Root>
  );
};

export default InterRoundLobby;
